import type { DOMEvent } from '@omq/types';

import type { FormField } from './form_field';

import { setStyle } from '../utils/helper';
import { CONDITION_CSS } from '../conditional-answer/conditional-answer';

/**
 * Class that handles form validation.
 *
 * @class
 * @author Florian Walch
 * @since 9.1
 */
export default class FormValidation {
  /**
   * Form DOM element.
   *
   * @type {HTMLFormElement}
   */
  form: HTMLFormElement;

  errorElement: HTMLElement | null | undefined = null;

  /**
   * Constructor
   *
   * @param form
   */
  constructor(form: HTMLFormElement) {
    this.form = form;

    this.initFormElements();
  }

  /**
   * Add event handlers to form elements.
   */
  initFormElements(): void {
    // iterate through form elements
    Array.prototype.forEach.call(this.form.elements, (element) => {
      element.addEventListener('invalid', this.handleInvalidEvent);

      element.addEventListener('input', this.handleInputEvent);

      element.addEventListener('focus', this.handleFocusEvent);

      element.addEventListener('blur', this.handleBlurEvents);
    });

    // add click handler to all condition option buttons
    // to reset invalid flag
    Array.from(this.form.querySelectorAll(`.${CONDITION_CSS.OPTION}`)).forEach((option) => {
      option.addEventListener('click', this.handleConditionOptionClick);
    });

    // add change handler to multi-select checkbox groups to enforce max selection limit
    Array.from(this.form.querySelectorAll('.multi-select-group')).forEach((group) => {
      group.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
        cb.addEventListener('change', this.handleMultiSelectChange);
      });
    });
  }

  unsubscribeElements() {
    // iterate through form elements
    Array.prototype.forEach.call(this.form.elements, (element) => {
      element.removeEventListener('invalid', this.handleInvalidEvent);

      element.removeEventListener('input', this.handleInputEvent);

      element.removeEventListener('focus', this.handleFocusEvent);

      element.removeEventListener('blur', this.handleBlurEvents);
    });

    // remove click handler from condition option buttons
    Array.from(this.form.querySelectorAll(`.${CONDITION_CSS.OPTION}`)).forEach((option) => {
      option.removeEventListener('click', this.handleConditionOptionClick);
    });

    // remove change handler from multi-select checkbox groups
    Array.from(this.form.querySelectorAll('.multi-select-group')).forEach((group) => {
      group.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
        cb.removeEventListener('change', this.handleMultiSelectChange);
      });
    });
  }

  /**
   * Validate form by checking validity of form elements,
   * validity of conditions and validity of textarea elements.
   */
  validate: () => boolean = (): boolean => {
    // return valid state of form
    const formIsValid = this.form.checkValidity();
    const conditionsAreValid = this.checkConditionValidity();
    const multiSelectsValid = this.checkMultiSelectValidity();
    const textAreasValid = Array.from(this.form.querySelectorAll('textarea')).every((element) => {
      return this.textAreaCheckValidity(element);
    });

    return formIsValid && conditionsAreValid && multiSelectsValid && textAreasValid;
  };

  /**
   * Handle invalid events. Is triggered if checkValidity is called,
   * or user tries to submit the form.
   *
   * @param {DOMEvent} event
   */
  handleInvalidEvent: (event: DOMEvent<FormField>) => void = (event) => {
    const element = event.currentTarget;

    // add invalid style
    element.classList.add('invalid');

    this.displayErrorMessageFor(element);

    // do not do browser default stuff (display native error message)
    event.preventDefault();
  };

  /**
   * Handle input events (content changes).
   * Check if input is valid, and remove error style/message.
   *
   * @param {DOMEvent} event
   */
  handleInputEvent: (event: DOMEvent<FormField>) => void = (event) => {
    const element = event.currentTarget;

    // if field is valid
    if (this.textAreaCheckValidity(element)) {
      // remove message
      this.removeErrorMessage();

      // remove style
      element.classList.remove('invalid');
    }
  };

  /**
   * Handle focus events.
   * If field is invalid on focus, display error box.
   *
   * @param {DOMEvent} event
   */
  handleFocusEvent: (event: DOMEvent<FormField>) => void = (event) => {
    const element = event.currentTarget;

    if (!this.textAreaCheckValidity(element)) {
      this.displayErrorMessageFor(element);
    }
  };

  /**
   * Handle blur events.
   *
   * If field is invalid on blur, check if its valid.
   * Remove error message.
   *
   * @param {DOMEvent} event
   */
  handleBlurEvents: (event: DOMEvent<FormField>) => void = (event) => {
    const element = event.currentTarget;

    this.textAreaCheckValidity(element);

    // remove message
    this.removeErrorMessage();
  };

  /**
   * Enforce max selection limit on multi-select checkbox groups.
   * When user checks more than data-max-select allows, uncheck the just-clicked checkbox.
   *
   * @param {Event} event
   */
  handleMultiSelectChange: (event: Event) => void = (event) => {
    const checkbox = event.currentTarget;
    if (!(checkbox instanceof HTMLInputElement)) {
      return;
    }

    const group = checkbox.closest('.multi-select-group');
    if (!group) {
      return;
    }

    const maxSelect = parseInt(group.getAttribute('data-max-select') || '0', 10);
    const checked = group.querySelectorAll('input[type="checkbox"]:checked');

    // enforce max-select: uncheck the just-clicked checkbox if over limit
    if (maxSelect > 0 && checked.length > maxSelect) {
      checkbox.checked = false;
    }

    // remove invalid class and error message if selection is now within bounds
    const minSelect = parseInt(group.getAttribute('data-min-select') || '0', 10);
    const checkedCount = group.querySelectorAll('input[type="checkbox"]:checked').length;
    const required = group.hasAttribute('data-required');
    if (
      !(required && checkedCount === 0) &&
      (minSelect === 0 || checkedCount >= minSelect) &&
      (maxSelect === 0 || checkedCount <= maxSelect)
    ) {
      group.classList.remove('invalid');
      this.removeMultiSelectErrorMessage(group);
    }
  };

  /**
   * Reset invalid state for condition options after they have been clicked/selected.
   *
   * @param event
   */
  handleConditionOptionClick: (event: MouseEvent) => void = (event) => {
    const option = event.currentTarget;
    if (!(option instanceof HTMLElement)) {
      return;
    }

    const conditionOptions = option.parentElement;
    if (conditionOptions == null) {
      return;
    }

    // remove invalid class from all options
    Array.from(conditionOptions.children).forEach((option) => {
      option.classList.remove('invalid');
    });
  };

  /**
   * Check if textarea pattern is valid.
   *
   * @param element - form element
   * @returns {boolean} - true if valid
   */
  textAreaCheckValidity(element: FormField): boolean {
    if (element instanceof HTMLTextAreaElement && element.getAttribute('pattern')) {
      const pattern = element.getAttribute('pattern') || '';
      let result = new RegExp(`^(?:${pattern})$`).test(element.value);

      if (!result) {
        const evt = new Event('invalid', {
          bubbles: true,
          cancelable: false,
          composed: true,
        });
        element.dispatchEvent(evt);
      }

      return result && element.checkValidity();
    } else {
      // if not a textarea, use default check
      return element.checkValidity();
    }
  }

  /**
   * Check if all visible & required conditions are valid/have a selection
   */
  checkConditionValidity(): boolean {
    const { form } = this;

    let isValid = true;

    // go through all required conditions
    Array.from(form.querySelectorAll(`.${CONDITION_CSS.ROOT} input[type=hidden]`)).forEach(
      (conditionValueField) => {
        if (!(conditionValueField instanceof HTMLInputElement)) {
          return;
        }

        // only handle fields inside conditions
        const condition = conditionValueField.closest(`.${CONDITION_CSS.ROOT}`);

        if (condition == null) {
          return;
        }

        // dont validate disabled fields
        if (conditionValueField.disabled || !conditionValueField.required) {
          return;
        }

        // if value is empty, no selection has been made
        if (conditionValueField.value === '') {
          // set valid flag to false
          // prevents form submit
          isValid = false;

          // get condition options
          const conditionOptions = Array.from(condition.children).find((child) =>
            child.matches('.' + CONDITION_CSS.OPTIONS),
          );

          if (conditionOptions != null) {
            // set invalid class to all condition buttons
            Array.from(conditionOptions.children).forEach((option) =>
              option.classList.add('invalid'),
            );
          }
        }
      },
    );

    // return flag
    return isValid;
  }

  /**
   * Check if all multi-select checkbox groups satisfy their min/max selection constraints.
   * Reads data-min-select, data-max-select, and data-required attributes from .multi-select-group containers.
   */
  checkMultiSelectValidity(): boolean {
    const { form } = this;
    let isValid = true;

    Array.from(form.querySelectorAll('.multi-select-group')).forEach((group) => {
      if (!(group instanceof HTMLElement)) {
        return;
      }

      const checkedCount = group.querySelectorAll('input[type="checkbox"]:checked').length;
      const minSelect = parseInt(group.getAttribute('data-min-select') || '0', 10);
      const maxSelect = parseInt(group.getAttribute('data-max-select') || '0', 10);
      const required = group.hasAttribute('data-required');

      if (required && checkedCount === 0) {
        isValid = false;
        group.classList.add('invalid');
        this.displayMultiSelectErrorMessage(group);
      } else if (minSelect > 0 && checkedCount < minSelect) {
        isValid = false;
        group.classList.add('invalid');
        this.displayMultiSelectErrorMessage(group);
      } else if (maxSelect > 0 && checkedCount > maxSelect) {
        isValid = false;
        group.classList.add('invalid');
        this.displayMultiSelectErrorMessage(group);
      }
    });

    return isValid;
  }

  /**
   * Display error message for a multi-select group.
   * Follows the same pattern as displayErrorMessageFor for input fields.
   *
   * @param {Element} group - multi-select group element
   */
  displayMultiSelectErrorMessage(group: Element): void {
    // remove existing error message for this group
    this.removeMultiSelectErrorMessage(group);

    // get message from attribute
    const message = group.getAttribute('data-validate-error-message');
    if (message == null || message === '') {
      return;
    }

    const parentElement = group.parentElement;
    if (parentElement == null) {
      return;
    }

    // create error box
    const errorElement = document.createElement('div');
    errorElement.className = 'form-error-message multi-select-error-message';
    errorElement.innerText = message;

    // get dimensions from parent element, to position error element
    const parentRect = parentElement.getClientRects()[0];
    errorElement.style.top = `${parentRect.height + 14}px`;
    errorElement.style.left = `0px`;

    // set parent to relative (message box is absolute positioned)
    if (parentElement instanceof HTMLElement) {
      setStyle(parentElement, {
        position: 'relative',
      });
    }

    // add error box after the group
    parentElement.insertBefore(errorElement, group.nextSibling);
  }

  /**
   * Remove error message from a multi-select group.
   *
   * @param {Element} group - multi-select group element
   */
  removeMultiSelectErrorMessage(group: Element): void {
    const errorElement = group.parentElement?.querySelector(
      '.multi-select-error-message',
    );
    if (errorElement != null) {
      errorElement.remove();
    }
  }

  /**
   * Display error message for passed HTMLElement.
   *
   * @param {FormField} element - Form element
   */
  displayErrorMessageFor(element: FormField): void {
    // remove existing error box
    this.removeErrorMessage();

    // get message from attribute
    let message = element.getAttribute('data-validate-error-message');

    // if there is no message, get default validation message
    // istanbul ignore else
    if (message == null) {
      message = element.validationMessage;
    }

    const parentElement = element.parentElement;
    if (parentElement == null) {
      throw new Error('Element needs parent element to show error message.');
    }

    // create error box
    const errorElement = document.createElement('div');
    errorElement.className = 'form-error-message';
    errorElement.innerText = message;

    // get dimensions from parent element, to position error element
    const parentRect = parentElement.getClientRects()[0];
    errorElement.style.top = `${parentRect.height + 14}px`;
    errorElement.style.left = `0px`;

    // istanbul ignore else
    if (parentElement instanceof HTMLElement) {
      // set parent to relative (message box is absolute positioned).
      setStyle(parentElement, {
        position: 'relative',
      });
    }

    // add error box
    parentElement.insertBefore(errorElement, element.nextSibling);
    this.errorElement = errorElement;
  }

  /**
   * Remove error message box.
   */
  removeErrorMessage(): void {
    // remove if element exists
    if (this.errorElement) {
      this.errorElement.remove();
    }

    // reset to null, for later checks
    this.errorElement = null;
  }
}
